import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Copy, Loader2, RefreshCw, SlidersHorizontal, Clock, Lock, Check } from "lucide-react";
import DashboardLayout from "@/components/DashboardLayout";
import { api } from "@/lib/api";
import { FOKUSTYP_NAME } from "@/data/quickTestData";
import { EuleGlyph, BieneGlyph, KatzeGlyph, HundGlyph } from "@/components/FokustypGlyphs";

const GLYPH = { eule: EuleGlyph, biene: BieneGlyph, katze: KatzeGlyph, hund: HundGlyph };
const FOKUSTYPS = ["katze", "eule", "biene", "hund"];

// Master-Prompt v2.9 §3 — Theorie-Anker (mirror backend)
const THEORIE_ANKER = {
  eule: "Eine E-Mail an eine Eule wirkt, wenn du auf der Sache bleibst und Argumente lieferst, nicht auf der Person.",
  biene: "Eine E-Mail an eine Biene wirkt, wenn du Klarheit und Reihenfolge schaffst, nicht Spontanes forderst.",
  katze: "Eine E-Mail an eine Katze wirkt, wenn du Neugier und Lust ansprichst, nicht Pflicht und Druck.",
  hund: "Eine E-Mail an einen Hund wirkt, wenn du Wertschätzung und Beziehung betonst, nicht Sachargumente.",
};

const TONALITAET_OPTIONEN = ["sachlich", "warm", "direkt", "freundlich", "neutral"];

function FokustypButton({ slug, selected, onClick, disabled }) {
  const Glyph = GLYPH[slug];
  return (
    <button
      type="button"
      onClick={() => onClick(slug)}
      disabled={disabled}
      data-testid={`cc-fokustyp-${slug}`}
      aria-pressed={selected}
      className={`flex flex-col items-center gap-2 rounded-xl border-2 p-3 transition-all disabled:opacity-50 ${
        selected
          ? "border-[var(--fts-primary-deep)] bg-[var(--fts-primary-deep)]/5"
          : "border-[var(--fts-neutral-200)] bg-white hover:border-[var(--fts-primary-bright)]"
      }`}
    >
      <Glyph size={36} />
      <span className="text-xs font-bold text-[var(--fts-text-primary)]">{FOKUSTYP_NAME[slug]}</span>
    </button>
  );
}

export default function CoCreator() {
  const navigate = useNavigate();
  const [anlass, setAnlass] = useState("");
  const [fokustyp, setFokustyp] = useState(null);
  const [tonalitaet, setTonalitaet] = useState("");
  const [tonalitaetSliderOpen, setTonalitaetSliderOpen] = useState(false);
  const [counter, setCounter] = useState(null);
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/co-creator/counter").then(
      ({ data }) => setCounter(data),
      () => setCounter(null),
    );
  }, []);

  const isFree = counter?.tier === "free";
  const used = counter?.verwendet ?? 0;
  const limit = counter?.limit ?? 3;
  const limitReached = isFree && used >= limit;
  const canSubmit = !!anlass.trim() && !!fokustyp && !loading && !limitReached;

  const updateCounter = (resp) => {
    if (resp?.verwendet !== undefined) {
      setCounter((c) => ({ ...c, verwendet: resp.verwendet, limit: resp.limit ?? c?.limit }));
    }
  };

  const handleGenerate = async () => {
    if (!canSubmit) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/co-creator/generate", {
        anlass: anlass.trim(),
        fokustyp,
        tonalitaet: tonalitaet || undefined,
      });
      setOutput(data);
      updateCounter(data);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 402) {
        setError(detail?.message || "Free-Limit erreicht. Schalte Pro Solo frei.");
      } else {
        setError(detail || "Generierung fehlgeschlagen. Bitte erneut versuchen.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVariant = async () => {
    if (!output || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/co-creator/variants", {
        anlass: anlass.trim(),
        fokustyp_aktuell: output.fokustyp,
        tonalitaet: tonalitaet || undefined,
      });
      setOutput(data);
      setFokustyp(data.fokustyp);
      updateCounter(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Variante fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleTonalityChange = async (newTonality) => {
    setTonalitaet(newTonality);
    if (!output || loading) return;
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.post("/co-creator/tonality", {
        anlass: anlass.trim(),
        fokustyp: output.fokustyp,
        tonalitaet: newTonality,
      });
      setOutput(data);
      updateCounter(data);
    } catch (e) {
      setError(e?.response?.data?.detail?.message || "Anpassung fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!output) return;
    const text = `Betreff: ${output.subject}\n\n${output.anrede}\n\n${output.body}\n\n${output.signoff}`;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setError("Kopieren nicht möglich. Bitte manuell markieren.");
    }
  };

  return (
    <DashboardLayout>
      {({ data: layoutData }) => {
        const firstName = layoutData?.user?.first_name;
        return (
          <div className="max-w-3xl mx-auto px-6 lg:px-8 py-12 space-y-6" data-testid="co-creator-page">
            <button
              onClick={() => navigate("/cockpit")}
              className="inline-flex items-center gap-1 text-sm font-semibold text-[var(--fts-text-muted)] hover:text-[var(--fts-primary-bright)]"
              data-testid="dashboard-cocreator-back"
            >
              <ArrowLeft className="w-4 h-4" /> Mitgliederbereich
            </button>

            <header>
              <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]" data-testid="cc-eyebrow">Der Werkzeugkasten</p>
              <h1
                className="mt-2 text-3xl sm:text-4xl font-extrabold tracking-tight text-[var(--fts-text-primary)]"
                data-testid="cc-headline"
              >
                {firstName ? `${firstName}, an wen richtet sich deine E-Mail?` : "An wen richtet sich deine E-Mail?"}
              </h1>
              <p className="mt-2 text-sm text-[var(--fts-text-muted)]">
                {isFree ? (
                  <span data-testid="cc-counter">
                    Free-Tier: <span className="font-semibold text-[var(--fts-text-primary)]">{used} von {limit}</span> Läufen genutzt.
                  </span>
                ) : (
                  <span data-testid="cc-counter">Pro Solo: unbegrenzte Läufe.</span>
                )}
              </p>
            </header>

            {/* Schritt 1: Theorie-Anker */}
            {fokustyp && (
              <div
                className="rounded-xl border border-[var(--fts-primary-bright)]/30 bg-[var(--fts-primary-bright)]/5 p-4"
                data-testid="cc-theorie-anker"
              >
                <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-deep)]">Warum dieser Fokustyp?</p>
                <p className="mt-1 text-sm text-[var(--fts-text-primary)] leading-relaxed">
                  {THEORIE_ANKER[fokustyp]}
                </p>
              </div>
            )}

            {/* Schritt 2: Input */}
            <section className="rounded-2xl border border-[var(--fts-neutral-200)] bg-white p-6 space-y-5" data-testid="cc-input-section">
              <div>
                <label className="text-sm font-semibold text-[var(--fts-text-primary)]" htmlFor="cc-anlass">
                  Anlass <span className="text-[var(--fts-accent)]">*</span>
                </label>
                <textarea
                  id="cc-anlass"
                  data-testid="cc-anlass-input"
                  value={anlass}
                  onChange={(e) => setAnlass(e.target.value.slice(0, 500))}
                  rows={3}
                  maxLength={500}
                  placeholder="z. B. Partnerin hat mich gestern falsch verstanden"
                  className="mt-2 w-full rounded-xl border border-[var(--fts-neutral-200)] px-4 py-3 text-sm focus:border-[var(--fts-primary-bright)] focus:outline-none resize-none"
                />
                <p className="mt-1 text-xs text-[var(--fts-text-muted)] text-right">{anlass.length}/500</p>
              </div>

              <div>
                <p className="text-sm font-semibold text-[var(--fts-text-primary)]">
                  Fokustyp <span className="text-[var(--fts-accent)]">*</span>
                </p>
                <div className="mt-2 grid grid-cols-4 gap-2" data-testid="cc-fokustyp-grid">
                  {FOKUSTYPS.map((slug) => (
                    <FokustypButton key={slug} slug={slug} selected={fokustyp === slug} onClick={setFokustyp} disabled={loading} />
                  ))}
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-[var(--fts-text-primary)]" htmlFor="cc-tonalitaet">
                  Tonalität <span className="text-xs font-normal text-[var(--fts-text-muted)]">(optional)</span>
                </label>
                <input
                  id="cc-tonalitaet"
                  data-testid="cc-tonalitaet-input"
                  type="text"
                  value={tonalitaet}
                  onChange={(e) => setTonalitaet(e.target.value.slice(0, 30))}
                  maxLength={30}
                  placeholder="z. B. sachlich, warm, direkt"
                  className="mt-2 w-full rounded-xl border border-[var(--fts-neutral-200)] px-4 py-3 text-sm focus:border-[var(--fts-primary-bright)] focus:outline-none"
                />
              </div>

              <button
                type="button"
                onClick={handleGenerate}
                disabled={!canSubmit}
                data-testid="cc-generate-btn"
                className="w-full rounded-xl bg-[var(--fts-primary-deep)] hover:bg-[var(--fts-primary-bright)] text-white font-bold px-5 py-4 text-base disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ArrowRight className="w-5 h-5" />}
                {loading ? "Generiere…" : "E-Mail generieren"}
              </button>
              {limitReached && (
                <div
                  className="rounded-xl border-2 border-[var(--fts-amber-free)] bg-amber-50/50 p-4"
                  data-testid="cc-paywall"
                >
                  <div className="flex items-start gap-3">
                    <Lock className="w-5 h-5 text-[var(--fts-accent)] mt-0.5" />
                    <div>
                      <p className="text-sm font-bold text-[var(--fts-text-primary)]">
                        {firstName ? `${firstName}, dein Free-Limit ist erreicht.` : "Free-Limit erreicht."}
                      </p>
                      <p className="mt-1 text-xs text-[var(--fts-text-muted)]">
                        Schalte Pro Solo frei für unbegrenzte E-Mails.
                      </p>
                      <button
                        type="button"
                        onClick={() => navigate("/dashboard/abo")}
                        data-testid="cc-paywall-cta"
                        className="mt-3 inline-flex items-center gap-1 rounded-lg bg-[var(--fts-success)] text-white font-bold px-4 py-2 text-xs"
                      >
              Pro Solo · 39 €/Monat <ArrowRight className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              )}
              {error && !limitReached && (
                <p className="text-sm text-[var(--fts-accent)]" data-testid="cc-error">{String(error)}</p>
              )}
            </section>

            {/* Schritt 3: Output */}
            {output && (
              <section
                className="rounded-2xl border-2 border-[var(--fts-success)] bg-white p-6 space-y-4"
                data-testid="cc-output-section"
              >
                <div className="flex items-start justify-between gap-3">
                  <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-success)]">
                    Deine E-Mail · {FOKUSTYP_NAME[output.fokustyp]}-Voice
                  </p>
                  <button
                    type="button"
                    onClick={handleCopy}
                    data-testid="cc-copy-btn"
                    className="inline-flex items-center gap-1 rounded-lg border border-[var(--fts-neutral-200)] hover:border-[var(--fts-primary-bright)] px-3 py-1.5 text-xs font-semibold text-[var(--fts-text-primary)]"
                  >
                    {copied ? <Check className="w-3 h-3 text-[var(--fts-success)]" /> : <Copy className="w-3 h-3" />}
                    {copied ? "Kopiert!" : "Kopieren"}
                  </button>
                </div>
                <div className="rounded-xl bg-[var(--fts-neutral-50)] p-5 font-sans space-y-3">
                  <p className="text-xs font-bold uppercase text-[var(--fts-text-muted)]">Betreff</p>
                  <p className="text-base font-bold text-[var(--fts-text-primary)]" data-testid="cc-output-subject">{output.subject}</p>
                  <div className="border-t border-[var(--fts-neutral-200)] pt-3 space-y-3">
                    <p className="text-sm text-[var(--fts-text-primary)]" data-testid="cc-output-anrede">{output.anrede}</p>
                    <p className="text-sm text-[var(--fts-text-primary)] leading-relaxed" data-testid="cc-output-body">{output.body}</p>
                    <p className="text-sm text-[var(--fts-text-primary)]" data-testid="cc-output-signoff">{output.signoff}</p>
                  </div>
                </div>
                <p
                  className="text-xs italic text-[var(--fts-text-muted)] leading-relaxed"
                  data-testid="cc-output-erklaer"
                >
                  {output.erklaer_satz}
                </p>

                {/* Schritt 4: Action-Buttons */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 pt-2 border-t border-[var(--fts-neutral-200)]" data-testid="cc-actions">
                  <button
                    type="button"
                    onClick={handleVariant}
                    disabled={loading || limitReached}
                    data-testid="cc-action-variant"
                    className="rounded-xl border border-[var(--fts-neutral-200)] hover:border-[var(--fts-primary-bright)] px-3 py-2.5 text-xs font-semibold text-[var(--fts-text-primary)] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <RefreshCw className="w-3 h-3" /> Andere Fokustyp-Variante
                  </button>
                  <button
                    type="button"
                    onClick={() => setTonalitaetSliderOpen((v) => !v)}
                    disabled={loading || limitReached}
                    data-testid="cc-action-tonality"
                    className="rounded-xl border border-[var(--fts-neutral-200)] hover:border-[var(--fts-primary-bright)] px-3 py-2.5 text-xs font-semibold text-[var(--fts-text-primary)] inline-flex items-center justify-center gap-1.5 disabled:opacity-50"
                  >
                    <SlidersHorizontal className="w-3 h-3" /> Tonalität anpassen
                  </button>
                  <button
                    type="button"
                    disabled
                    data-testid="cc-action-sequence"
                    title="In v2 verfügbar"
                    className="rounded-xl border border-dashed border-[var(--fts-neutral-200)] px-3 py-2.5 text-xs font-semibold text-[var(--fts-text-muted)] inline-flex items-center justify-center gap-1.5 cursor-not-allowed"
                  >
                    <Clock className="w-3 h-3" /> 3 Tage später
                  </button>
                </div>

                {tonalitaetSliderOpen && (
                  <div
                    className="rounded-xl bg-[var(--fts-neutral-50)] border border-[var(--fts-neutral-200)] p-4 space-y-2"
                    data-testid="cc-tonality-slider"
                  >
                    <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-text-muted)]">Tonalität wählen</p>
                    <div className="flex flex-wrap gap-2">
                      {TONALITAET_OPTIONEN.map((t) => (
                        <button
                          key={t}
                          type="button"
                          onClick={() => handleTonalityChange(t)}
                          disabled={loading || limitReached}
                          data-testid={`cc-tonality-option-${t}`}
                          className={`rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors disabled:opacity-50 ${
                            tonalitaet === t
                              ? "bg-[var(--fts-primary-deep)] text-white border-[var(--fts-primary-deep)]"
                              : "bg-white text-[var(--fts-text-primary)] border-[var(--fts-neutral-200)] hover:border-[var(--fts-primary-bright)]"
                          }`}
                        >
                          {t}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </section>
            )}
          </div>
        );
      }}
    </DashboardLayout>
  );
}
